import { Link, useNavigate, useParams } from 'react-router-dom';
import { listByRange } from '../../db/entries';
import { getCategoryMap } from '../../db/categories';
import { emptyProjects, getProjectsDoc } from '../../db/projects';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { EntryCard } from '../components/EntryCard';
import { WeeklyReviewCard } from '../components/WeeklyReviewCard';
import { TaskChecklist } from '../components/TaskChecklist';
import {
  addDays,
  isToday,
  startOfWeek,
  toDateKey,
  toWeekKey,
  weekKeyToStartDate,
} from '../../lib/datetime';
import type { Category, ProjectsDoc } from '../../domain/types';

const WEEKDAY = ['月', '火', '水', '木', '金', '土', '日'];

export function WeekView({ weekKey }: { weekKey?: string } = {}) {
  const version = useDataVersion();
  const params = useParams();
  const week = weekKey ?? params.week ?? toWeekKey(new Date());
  const navigate = useNavigate();
  const start = weekKeyToStartDate(week);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const end = days[6];

  const entries = useAsync(
    () => listByRange(toDateKey(start), toDateKey(end)),
    [week, version],
    [],
  );
  const categories = useAsync<Map<string, Category>>(getCategoryMap, [version], new Map());
  const projects = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());

  // その週の中なら今日、範囲外ならその週の初日に対して作る
  const now = new Date();
  const defaultNewDate = now >= start && now <= end ? now : start;

  const byDay = new Map<string, typeof entries>();
  for (const e of entries) {
    const list = byDay.get(e.dateKey) ?? [];
    list.push(e);
    byDay.set(e.dateKey, list);
  }

  return (
    <div className="view">
      <header className="view-head">
        <button
          className="nav-btn"
          onClick={() => navigate(`/week/${toWeekKey(addDays(start, -7))}`)}
          aria-label="前の週"
        >
          ‹
        </button>
        <div className="view-title">
          <h1>
            {start.getFullYear()}年{start.getMonth() + 1}月{start.getDate()}日 〜{' '}
            {end.getMonth() + 1}月{end.getDate()}日
          </h1>
        </div>
        <button
          className="nav-btn"
          onClick={() => navigate(`/week/${toWeekKey(addDays(start, 7))}`)}
          aria-label="次の週"
        >
          ›
        </button>
        <button className="today-btn" onClick={() => navigate(`/week/${toWeekKey(new Date())}`)}>
          今週
        </button>
        <Link className="primary-btn head-new" to={`/new?date=${toDateKey(defaultNewDate)}`}>
          ＋ 新規
        </Link>
      </header>

      <div className="view-body">
        <TaskChecklist kind="weekly" period={week} />
        <WeeklyReviewCard week={week} isPast={start < startOfWeek(new Date())} />
        <div className="week-grid">
        {days.map((d, i) => {
          const key = toDateKey(d);
          const list = byDay.get(key) ?? [];
          return (
            <div key={key} className={`week-col${isToday(d) ? ' is-today' : ''}`}>
              <button className="week-col-head" onClick={() => navigate(`/day/${key}`)}>
                <span className="week-dow">{WEEKDAY[i]}</span>
                <span className="week-day">{d.getDate()}</span>
              </button>
              <div className="week-col-body">
                {list.length === 0 ? (
                  <div className="week-empty">—</div>
                ) : (
                  list.map((e) => (
                    <EntryCard key={e.id} entry={e} categories={categories} projects={projects} compact />
                  ))
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
